import {
  getTimeAssignedByGrade,
  getManifestByResourceType,
  handleCreateUpdateAssignments,
  getMostCommonGrade,
} from "@/utilities";
import { getSkillTimeBalance } from "@/utilities";
import { getSkillResources } from "@/features/planner/utils/getSkillResources";
import { updateCRUDAssignmentsQueue } from "@/store/slices";
import { AssignmentType, MasteryStatusPacing, ResourceType, SkillType } from "@/enums";
import { AmiraResources, LessonDetails } from "@/models";
import { AssignmentManifest } from "@/models/Assignment";

export enum ResourceTypeLower {
  teacherResource = "teacherresource",
  istationLegacyActivity = "istationlegacyactivity",
  tutor = "tutor",
  skillTutor = "skilltutor",
  erss = "erss",
  textSet = "textset",
  microLesson = "microlesson"
}

export const ASSIGNMENT_PRIORITY_ORDER_REMOVAL = [
  ResourceTypeLower.teacherResource,
  ResourceTypeLower.istationLegacyActivity,
  ResourceTypeLower.tutor,
  ResourceTypeLower.skillTutor,
  ResourceTypeLower.erss,
  ResourceTypeLower.textSet,
  ResourceTypeLower.microLesson,
] as const;

export const ASSIGNMENT_PRIORITY_ORDER_ADDITION = [
  ResourceTypeLower.tutor,
  ResourceTypeLower.skillTutor,
  ResourceTypeLower.erss,
  ResourceTypeLower.textSet,
  ResourceTypeLower.microLesson,
  ResourceTypeLower.istationLegacyActivity,
  ResourceTypeLower.teacherResource,
] as const;

export const CONTENT_CATEGORIES = {
  teacherResource: [ResourceTypeLower.teacherResource],
  microLessonAndLegacy: [ResourceTypeLower.microLesson, ResourceTypeLower.istationLegacyActivity],
  tutorAndSupports: [ResourceTypeLower.tutor, ResourceTypeLower.skillTutor, ResourceTypeLower.erss, ResourceTypeLower.textSet],
} as const;
export interface StudentSkillBookingData {
  studentId: string;
  skillId: string;
  excessTime: number;
  shortfallTime: number;
  assignments: AssignmentManifest[];
  timeAssigned: number;
  timeToGrowth: number;
}

export const isTeacherResourceNotForSkill = (
  resource: AmiraResources,
  skillId: string
) => {
  return (
    resource.resourceType === ResourceType.teacherResource &&
    (resource.tags?.includes("LESSONNAME-") ||
      resource.tags?.includes("CURR-") ||
      !resource.tags?.includes("SKILLDEFAULT-ASSIGNED") ||
      !resource.tags?.includes(`SKILL-${skillId}`))
  );
};

const THRESHOLD_FOR_AUTO_FIX = 2;

// ============================================================================
// CORE UTILITY FUNCTIONS
// ============================================================================

// Student Assignment Helpers
const getStudentAssignmentFromSkillData = (
  studentId: string,
  lesson: LessonDetails,
) => {
  let studentAssignment = lesson.studentAssignments.find(
    (assignment) =>
      assignment.entityId === studentId
  );
  if (!studentAssignment) {
    studentAssignment = lesson.mainAssignment;
  } else if (studentAssignment.assignmentType === AssignmentType.SKIP_OVERRIDE) {
    return null;
  }

  return studentAssignment;
};

const normalizeManifest = (manifest: any[]) => {
  return manifest.map((entry: any) => ({
    storyId: entry.storyId,
    activityType: entry.activityType,
    skills:
      entry.skills?.map((skill: any) => ({ skillId: skill.skillId })) || [],
  }));
};

const getCategoryCounts = (manifest: any[], teacherResources: string[]) => {
  const categoryCounts = {
    teacherResource: 0,
    microLessonAndLegacy: 0,
    tutorAndSupports: 0,
  };

  categoryCounts.teacherResource = teacherResources.length;

  manifest.forEach((item) => {
    const activityType = item.activityType?.toLowerCase();
    if (
      CONTENT_CATEGORIES.microLessonAndLegacy.includes(activityType as any)
    ) {
      categoryCounts.microLessonAndLegacy++;
    } else if (
      CONTENT_CATEGORIES.tutorAndSupports.includes(activityType as any)
    ) {
      categoryCounts.tutorAndSupports++;
    }
  });

  return categoryCounts;
};

const areCategoriesBalanced = (categoryCounts: Record<string, number>) => {
  const counts = Object.values(categoryCounts).filter((c) => c > 0);
  if (counts.length === 0) return true;
  const maxCount = Math.max(...counts);
  const minCount = Math.min(...counts);
  return maxCount - minCount < 0;
};

const filterManifestBySkill = (manifest: any[], skillId: string) => {
  return manifest.filter((item) =>
    item.skills?.some((skill: any) => skill.skillId === skillId)
  );
};

const removeManifestItemsForSkill = (
  manifest: any[],
  skillId: string,
  itemsToRemove: any[]
) => {
  return manifest.filter((manifestItem) => {
    if (!manifestItem.skills?.some((skill: any) => skill.skillId === skillId)) {
      return true;
    }

    const shouldRemove = itemsToRemove.some(
      (itemToRemove) =>
        manifestItem.storyId === itemToRemove.storyId &&
        manifestItem.activityType === itemToRemove.activityType
    );

    return !shouldRemove;
  });
};

const isResourceDuplicate = (
  manifestEntries: any[],
  currentManifest: any[],
  skillId: string
): boolean => {
  return manifestEntries.some((entry) => {
    const entryId = entry.storyId;
    return currentManifest.some((existing) => {
      const existingId = existing.storyId;
      const existingSkillId = existing.skills?.[0]?.skillId;
      return entryId === existingId && existingSkillId === skillId;
    });
  });
};

// ============================================================================
// MANIFEST MODIFICATION FUNCTIONS
// ============================================================================

// Removal Functions
const removeByPriorityForSkill = (
  manifest: any[],
  skillId: string,
  targetTime: number,
  gradeLevel: any[],
  studentName: string,
  teacherResources: string[]
) => {
  const skillManifest = filterManifestBySkill(manifest, skillId);
  let currentSkillManifest = [...skillManifest];
  let newTeacherResources = [...teacherResources];
  let totalTimeRemoved = 0;
  const itemsToRemove: any[] = [];

  for (const activityType of ASSIGNMENT_PRIORITY_ORDER_REMOVAL) {
    if (totalTimeRemoved >= targetTime) break;

    if (activityType === ResourceTypeLower.teacherResource) {
      newTeacherResources.pop();
      totalTimeRemoved += getTimeAssignedByGrade(
        ResourceType.teacherResource,
        gradeLevel
      );
      continue;
    }
    const itemsOfType = currentSkillManifest.filter(
      (item) => item.activityType?.toLowerCase() === activityType
    );

    for (const item of itemsOfType) {
      if (totalTimeRemoved >= targetTime) break;

      const itemTime = getTimeAssignedByGrade(
        item.activityType || "",
        gradeLevel
      );
      itemsToRemove.push(item);
      currentSkillManifest = currentSkillManifest.filter(
        (manifestItem) =>
          !(
            manifestItem.storyId === item.storyId &&
            manifestItem.activityType === item.activityType
          )
      );
      totalTimeRemoved += itemTime;
    }
  }

  const updatedManifest = removeManifestItemsForSkill(
    manifest,
    skillId,
    itemsToRemove
  );
  return { manifest: updatedManifest, timeRemoved: totalTimeRemoved, teacherResources: newTeacherResources };
};

const balanceCategoriesForRemovalBySkill = (
  manifest: any[],
  skillId: string,
  targetTime: number,
  gradeLevel: any[],
  teacherResources: string[]
) => {
  const skillManifest = filterManifestBySkill(manifest, skillId);
  let currentSkillManifest = [...skillManifest];
  let newTeacherResources = [...teacherResources];
  let totalTimeRemoved = 0;
  const itemsToRemove: any[] = [];

  while (totalTimeRemoved < targetTime) {
    const categoryCounts = getCategoryCounts(currentSkillManifest, newTeacherResources);

    if (areCategoriesBalanced(categoryCounts)) {
      break;
    }

    const maxCategory = Object.entries(categoryCounts).reduce(
      (max, [category, count]) =>
        count > max.count ? { category, count } : max,
      { category: "", count: 0 }
    );

    if (maxCategory.count === 0) break;

    const categoryTypes =
      CONTENT_CATEGORIES[
        maxCategory.category as keyof typeof CONTENT_CATEGORIES
      ];
    let itemToRemove = currentSkillManifest.find((item) => {
      const activityType = item.activityType?.toLowerCase();
      return (
        activityType &&
        (categoryTypes as readonly string[]).includes(activityType)
      );
    });

    if (itemToRemove) {
      const itemTime = getTimeAssignedByGrade(
        itemToRemove.activityType || "",
        gradeLevel
      );
      itemsToRemove.push(itemToRemove);
      currentSkillManifest = currentSkillManifest.filter(
        (manifestItem) =>
          !(
            manifestItem.storyId === itemToRemove.storyId &&
            manifestItem.activityType === itemToRemove.activityType &&
            manifestItem.skills?.[0]?.skillId === itemToRemove.skills?.[0]?.skillId
          )
      );
      totalTimeRemoved += itemTime;
    } else if ((categoryTypes as readonly string[]).includes(ResourceTypeLower.teacherResource)) {
      newTeacherResources.pop();
      totalTimeRemoved += getTimeAssignedByGrade(
        ResourceType.teacherResource,
        gradeLevel
      );
    } else {
      break;
    }
  }

  const updatedManifest = removeManifestItemsForSkill(
    manifest,
    skillId,
    itemsToRemove
  );
  return { manifest: updatedManifest, timeRemoved: totalTimeRemoved, teacherResources: newTeacherResources };
};

// Addition Functions
const balanceCategoriesForAdditionBySkill = (
  currentManifest: any[],
  availableResources: any[],
  skillId: string,
  shortfallTime: number,
  gradeLevel: { grade: string }[] | [],
  teacherResources: string[],
  isOnlyOneSkill: boolean
) => {
  let manifest = [...currentManifest];
  let newTeacherResources = [...teacherResources];
  let remainingShortfall = shortfallTime;
  let skillManifest = filterManifestBySkill(manifest, skillId);

  const hasRemainingShortfall = () => remainingShortfall > 0;
  const hasUnusedResources = () =>
    availableResources.some((resource) => resource.targetSkillId === skillId);
  const skippedResources: string[] = [];

  while (hasRemainingShortfall() && hasUnusedResources()) {
    const categoryCounts = getCategoryCounts(skillManifest, newTeacherResources);

    if (areCategoriesBalanced(categoryCounts)) {
      break;
    }

    const minCategory = Object.entries(categoryCounts).reduce(
      (min, [category, count]) =>
        count < min.count && !skippedResources.includes(category) ? { category, count } : min,
      { category: "", count: Infinity }
    );

    if (minCategory.category === "") break;

    const categoryTypes =
      CONTENT_CATEGORIES[
        minCategory.category as keyof typeof CONTENT_CATEGORIES
      ];
    let resourceAdded = false;
    let resourceSkipped = false;

    for (let i = 0; i < availableResources.length && !resourceAdded; i++) {
      const resource = availableResources[i];
      const resourceType = resource.resourceType?.toLowerCase();
      if (!isOnlyOneSkill && isTeacherResourceNotForSkill(resource, skillId)) {
        continue;
      }

      if (
        resourceType &&
        (categoryTypes as readonly string[]).includes(resourceType) &&
        resource.targetSkillId === skillId
      ) {
        const itemTime = getTimeAssignedByGrade(
          resource.resourceType || "",
          gradeLevel
        );
        // If the resource is too large, skip it and try different resource types
        if (itemTime > remainingShortfall + THRESHOLD_FOR_AUTO_FIX) {
          resourceSkipped = true;
          skippedResources.push(minCategory.category);
          continue;
        }
        const manifestEntries = getManifestByResourceType(resource, skillId);
        const simplifiedManifestEntries = manifestEntries.map((entry) => ({
          storyId: entry.storyId,
          activityType: entry.activityType,
          skills: entry.skills
            ?.filter((skill) => skill.skillId === skillId)
            .map((skill) => ({ skillId: skill.skillId })),
        }));

        if (resource.resourceType === ResourceType.teacherResource) {
          newTeacherResources.push(resource.resourceUniqueId);
        } else {
          // make sure we don't add the same resource twice
          if (manifest.map(m => m.storyId).some(storyId => simplifiedManifestEntries.map(m => m.storyId).includes(storyId))) {
            continue;
          }
          manifest = [...manifest, ...simplifiedManifestEntries];
          skillManifest = [...skillManifest, ...simplifiedManifestEntries]; // update skillManifest to include the new resources, so we can use correct category counts
        }

        remainingShortfall = Math.max(0, remainingShortfall - itemTime);
        resourceAdded = true;
      }
    }

    if (!resourceAdded && !resourceSkipped) break;
    if (skippedResources.length === Object.keys(categoryCounts).length) break;
  }

  return { manifest, remainingShortfall, teacherResources: newTeacherResources };
};

const addByPriorityForSkill = (
  manifest: any[],
  availableResources: any[],
  skillId: string,
  shortfallTime: number,
  gradeLevel: { grade: string }[] | [],
  teacherResources: string[],
  isOnlyOneSkill: boolean
) => {
  let currentManifest = [...manifest];
  let newTeacherResources = [...teacherResources];
  let remainingShortfall = shortfallTime;

  for (const activityType of ASSIGNMENT_PRIORITY_ORDER_ADDITION) {
    if (
      remainingShortfall <= 0 ||
      !availableResources.some((resource) => resource.targetSkillId === skillId)
    )
      break;

    for (
      let i = 0;
      i < availableResources.length && remainingShortfall > 0;
      i++
    ) {
      const resource = availableResources[i];

      if (!isOnlyOneSkill && isTeacherResourceNotForSkill(resource, skillId)) {
        continue;
      }

      if (
        resource.resourceType?.toLowerCase() === activityType &&
        resource.targetSkillId === skillId
      ) {
        const itemTime = getTimeAssignedByGrade(
          resource.resourceType || "",
          gradeLevel
        );
        const manifestEntries = getManifestByResourceType(resource, skillId);
        const simplifiedManifestEntries = manifestEntries.map((entry) => ({
          storyId: entry.storyId,
          activityType: entry.activityType,
          skills: entry.skills
            ?.filter((skill) => skill.skillId === skillId)
            .map((skill) => ({ skillId: skill.skillId })),
        }));

        if (resource.resourceType === ResourceType.teacherResource) {
          newTeacherResources.push(resource.resourceUniqueId);
        } else {
          currentManifest = [...currentManifest, ...simplifiedManifestEntries];
        }

        remainingShortfall = Math.max(0, remainingShortfall - itemTime);
      }
    }
  }

  return { manifest: currentManifest, teacherResources: newTeacherResources };
};

// ============================================================================
// STUDENT MANIFEST UPDATE FUNCTIONS
// ============================================================================

export const updateStudentManifests = (
  updatedManifests: Array<{ studentId: string; manifest: any[], teacherResources: string[] }>,
  lesson: LessonDetails,
  dispatch: any
): Promise<number> => {
  return new Promise((resolve) => {
    if (updatedManifests.length === 0) {
      resolve(0);
      return;
    }

    const originalStudentGroupManifests: { studentId: string, manifest: AssignmentManifest[] }[] = [];
    for (const student of updatedManifests) {
      const studentAssignment = getStudentAssignmentFromSkillData(student.studentId, lesson);
      if (studentAssignment) {
        originalStudentGroupManifests.push({ studentId: student.studentId, manifest: [studentAssignment.manifest[0]] });
      }
    }

    const result = handleCreateUpdateAssignments({
      lesson,
      userId: lesson.userId,
      classroomId: lesson.classroomId,
      newStudentManifests: updatedManifests,
      teacherResources: updatedManifests.map((manifest) => ({ studentId: manifest.studentId, resources: manifest.teacherResources })),
      originalStudentGroupManifests,
    });

    if (result) {
      dispatch(updateCRUDAssignmentsQueue(result));
      setTimeout(() => resolve(updatedManifests.length), 0);
    } else {
      resolve(0);
    }
  });
};

// ============================================================================
// BOOKING CALCULATION FUNCTIONS
// ============================================================================

export const calculateStudentSkillBookingData = (
  studentId: string,
  skillId: string,
  lesson: LessonDetails,
  skills: any[],
): StudentSkillBookingData | null => {
  const studentAssignment = getStudentAssignmentFromSkillData(
    studentId,
    lesson
  );

  if (!studentAssignment) {
    console.log(
      `❌ No student assignment found for ${studentId} and skill ${skillId}`
    );
    return null;
  }

  const skill = lesson.skills?.find((s) => s.skillId === skillId);
  if (!skill) {
    console.log(`❌ Skill ${skillId} not found in lesson skills`);
    return null;
  }

  let groupName = MasteryStatusPacing.NO_DATA;
  const skillGroup = skills.find((s) => s.skill.skillId === skillId);
  if (skillGroup) {
    const studentGroup = skillGroup.studentGroups.find((sg: any) =>
      sg.students.some((s: any) => s.student.id === studentId)
    );
    if (studentGroup) {
      groupName = studentGroup.groupName as MasteryStatusPacing;
    }
  }

  const timeAssigned =
    lesson.timeAssigned?.[skillId]?.[groupName]?.[studentId] || 0;

  const timeBalance = getSkillTimeBalance(
    skill.timeToMaster || 0,
    timeAssigned,
    groupName
  );

  const timeToGrowth = Number(timeBalance.timeToGrowth) || 0;

  if (timeBalance.timeToGrowth === "--") {
    return null;
  }

  const excessTime = Math.max(0, timeAssigned - timeToGrowth - THRESHOLD_FOR_AUTO_FIX);
  const shortfallTime = Math.max(0, timeToGrowth - timeAssigned - THRESHOLD_FOR_AUTO_FIX);

  if (excessTime <= 0 && shortfallTime <= 0) {
    console.log(
      `❌ No excess or shortfall for skill ${skillId}, returning null`
    );
    return null;
  }

  return {
    studentId,
    skillId,
    excessTime,
    shortfallTime,
    assignments: studentAssignment.manifest || [],
    timeAssigned,
    timeToGrowth,
  };
};

// ============================================================================
// SKILL PROCESSING FUNCTIONS
// ============================================================================

// Individual Student Processing
const processOverbookedStudentSkill = (
  studentId: string,
  skillId: string,
  lesson: LessonDetails,
  skills: any[],
) => {
  const studentAssignment = getStudentAssignmentFromSkillData(
    studentId,
    lesson
  );
  if (!studentAssignment) return null;

  const student = lesson.students?.find((s) => s.id === studentId);
  const mostCommonGrade = getMostCommonGrade(lesson.students.map(s => ({ grade: s.grade.toString() })));
  const gradeLevel = mostCommonGrade ? [{ grade: mostCommonGrade.toString() }] : [];

  const overbookedData = calculateStudentSkillBookingData(
    studentId,
    skillId,
    lesson,
    skills,
  );
  if (!overbookedData || overbookedData.excessTime <= 0) {
    return null;
  }

  let currentManifest = normalizeManifest(
    studentAssignment.manifest || []
  ).filter((item) =>
    item.skills?.some((skill: any) => skill.skillId === skillId)
  );

  let newTeacherResources = [...studentAssignment.resources || []];

  const balanceResult = balanceCategoriesForRemovalBySkill(
    currentManifest,
    skillId,
    overbookedData.excessTime,
    gradeLevel,
    newTeacherResources
  );

  currentManifest = balanceResult.manifest;
  newTeacherResources = balanceResult.teacherResources;
  let totalTimeRemoved = balanceResult.timeRemoved;

  if (totalTimeRemoved < overbookedData.excessTime) {
    const remainingToRemove = overbookedData.excessTime - totalTimeRemoved;

    const priorityResult = removeByPriorityForSkill(
      currentManifest,
      skillId,
      remainingToRemove,
      gradeLevel,
      student?.first_name + " " + student?.last_name || `Student ${studentId}`,
      newTeacherResources
    );

    currentManifest = priorityResult.manifest;
    newTeacherResources = priorityResult.teacherResources;
  }

  return { manifest: currentManifest, teacherResources: newTeacherResources };
};

const processUnderbookedStudentSkill = (
  studentId: string,
  skillId: string,
  lesson: LessonDetails,
  skills: any[],
) => {
  const studentAssignment = getStudentAssignmentFromSkillData(
    studentId,
    lesson
  );
  if (!studentAssignment) return null;

  const student = lesson.students?.find((s) => s.id === studentId);
  const mostCommonGrade = getMostCommonGrade(lesson.students.map(s => ({ grade: s.grade.toString() })));
  const gradeLevel = mostCommonGrade ? [{ grade: mostCommonGrade.toString() }] : [];

  const underbookedData = calculateStudentSkillBookingData(
    studentId,
    skillId,
    lesson,
    skills,
  );
  if (!underbookedData || underbookedData.shortfallTime <= 0) {
    return null;
  }

  let currentManifest = normalizeManifest(studentAssignment.manifest || []);
  let newTeacherResources = [...studentAssignment.resources || []];

  const skill = lesson.skills?.find((s) => s.skillId === skillId);
  if (!skill) return null;

  const skillResources = getSkillResources({
    skillId: skillId,
    skillResources: lesson.skillResources,
    lessonName: lesson.lessonName || lesson.name,
    curriculumId: lesson.curriculumId,
    lessonSkills: lesson.skills.filter(s =>s.skillType === SkillType.FOCUS).map(s => ({skillId: s.skillId || "", displayName: s.displayName || ""}))
  });

  const availableResources: any[] = [];

  skillResources.forEach(resource => {
    const storyId =
      resource.storyIds && resource.storyIds.length > 0
        ? resource.storyIds[0]
        : resource.resourceUniqueId;
    if (resource.resourceType === ResourceType.teacherResource) {
      const isDuplicate = newTeacherResources.includes(resource.resourceUniqueId);
      if (!isDuplicate) {
        availableResources.push({
          ...resource,
          targetSkillId: skillId,
        });
      }
    } else {
      const manifestEntries = getManifestByResourceType(resource, skillId);
      const isDuplicate = isResourceDuplicate(
        manifestEntries,
        currentManifest,
        skillId
      );

      if (!isDuplicate && storyId) {
        availableResources.push({
          ...resource,
          targetSkillId: skillId,
        });
      }
    }
  });

  if (availableResources.length === 0) {
    return null;
  }

  const balanceResult = balanceCategoriesForAdditionBySkill(
    currentManifest,
    availableResources,
    skillId,
    underbookedData.shortfallTime,
    gradeLevel,
    newTeacherResources,
    lesson.skills.length === 1,
  );

  currentManifest = balanceResult.manifest;
  newTeacherResources = balanceResult.teacherResources;
  let remainingShortfall = balanceResult.remainingShortfall;

  if (remainingShortfall > 0) {
    const addByPriorityResult = addByPriorityForSkill(
      currentManifest,
      availableResources,
      skillId,
      remainingShortfall,
      gradeLevel,
      newTeacherResources,
      lesson.skills.length === 1
    );
    currentManifest = addByPriorityResult.manifest;
    newTeacherResources = addByPriorityResult.teacherResources;
  }

  return { manifest: currentManifest, teacherResources: newTeacherResources };
};

// Group Processing
const processSkillForAllStudents = (
  skillId: string,
  lesson: LessonDetails,
  skills: any[],
  isOverbooked: boolean
) => {
  const skill = lesson.skills?.find((s) => s.skillId === skillId);
  if (!skill) return [];

  const studentsNeedingSkill =
    lesson.students?.filter((student) => {
      const bookingData = calculateStudentSkillBookingData(
        student.id,
        skillId,
        lesson,
        skills,
      );
      return (
        bookingData &&
        (isOverbooked
          ? bookingData.excessTime > 0
          : bookingData.shortfallTime > 0)
      );
    }) || [];

  const updatedManifests: Array<{ studentId: string; manifest: any[], teacherResources: string[] }> = [];

  for (const student of studentsNeedingSkill) {
    const studentAssignment = getStudentAssignmentFromSkillData(
      student.id,
      lesson
    );
    if (!studentAssignment) continue;

    const bookingData = calculateStudentSkillBookingData(
      student.id,
      skillId,
      lesson,
      skills,
    );
    if (!bookingData) continue;

    let currentManifest = normalizeManifest(studentAssignment.manifest || []);
    let newTeacherResources = [...studentAssignment.resources || []];
    let hasChanges = false;

    if (isOverbooked && bookingData.excessTime > 0) {
      const processedManifest = processOverbookedStudentSkill(
        student.id,
        skillId,
        lesson,
        skills,
      );
      if (processedManifest) {
        currentManifest = processedManifest.manifest;
        newTeacherResources = processedManifest.teacherResources;
        hasChanges = true;
      }
    } else if (!isOverbooked && bookingData.shortfallTime > 0) {
      const processedManifest = processUnderbookedStudentSkill(
        student.id,
        skillId,
        lesson,
        skills,
      );
      if (processedManifest) {
        currentManifest = processedManifest.manifest;
        newTeacherResources = processedManifest.teacherResources;
        hasChanges = true;
      }
    }

    if (hasChanges) {
      updatedManifests.push({
        studentId: student.id,
        manifest: currentManifest,
        teacherResources: newTeacherResources,
      });
    }
  }

  return updatedManifests;
};

// ============================================================================
// MAIN HANDLER FUNCTIONS
// ============================================================================

export const handleOverbookedStudents = async (
  overbookedStudentIds: string[],
  lesson: LessonDetails,
  dispatch: any,
  skills: any[],
) => {
  const studentManifests = new Map<string, any[]>();
  const studentTeacherResources = new Map<string, string[]>();

  lesson.skills?.forEach((skill) => {
    if (!skill.skillId) return;
    const skillManifests = processSkillForAllStudents(
      skill.skillId,
      lesson,
      skills,
      true
    );
    skillManifests.forEach(({ studentId, manifest, teacherResources }) => {
      const existingManifest = studentManifests.get(studentId) || [];
      const existingTeacherResources = studentTeacherResources.get(studentId) || [];
      const filteredExistingManifest = existingManifest.filter(
        (entry) =>
          !entry.skills?.some((skill: any) => skill.skillId === skill.skillId)
      );
      const filteredExistingTeacherResources = existingTeacherResources.filter(
        (resource) => !teacherResources.includes(resource)
      );
      const filteredCurrentManifest = manifest.filter((item) =>
        item.skills?.some((skill: any) => skill.skillId === skill.skillId)
      );

      const combinedManifest = [
        ...filteredExistingManifest,
        ...filteredCurrentManifest,
      ];
      const combinedTeacherResources = [
        ...filteredExistingTeacherResources,
        ...teacherResources,
      ];

      studentManifests.set(studentId, combinedManifest);
      studentTeacherResources.set(studentId, combinedTeacherResources);
    });
  });

  const updatedManifests = Array.from(studentManifests.entries()).map(
    ([studentId, manifest]) => ({
      studentId,
      manifest,
      teacherResources: studentTeacherResources.get(studentId) || [],
    })
  );

  await updateStudentManifests(updatedManifests, lesson, dispatch);
  return studentManifests;
};

export const handleUnderbookedStudents = (
  underbookedStudentIds: string[],
  lesson: LessonDetails,
  dispatch: any,
  skills: any[],
  existingManifests?: Map<string, any[]>
) => {
  const studentManifests = existingManifests || new Map<string, any[]>();
  const studentTeacherResources = new Map<string, string[]>();

  lesson.skills?.forEach((skill) => {
    if (!skill.skillId) return;
    const skillManifests = processSkillForAllStudents(
      skill.skillId,
      lesson,
      skills,
      false
    );
    skillManifests.forEach(({ studentId, manifest, teacherResources }) => {
      const existingManifest = studentManifests.get(studentId) || [];
      const existingTeacherResources = studentTeacherResources.get(studentId) || [];
      const filteredExistingManifest = existingManifest.filter(
        (entry) =>
          !entry.skills?.some((skill: any) => skill.skillId === skill.skillId)
      );
      const filteredExistingTeacherResources = existingTeacherResources.filter(
        (resource) => !teacherResources.includes(resource)
      );
      const filteredCurrentManifest = manifest.filter((item) =>
        item.skills?.some((skill: any) => skill.skillId === skill.skillId)
      );

      const combinedManifest = [
        ...filteredExistingManifest,
        ...filteredCurrentManifest,
      ];
      const combinedTeacherResources = [
        ...filteredExistingTeacherResources,
        ...teacherResources,
      ];

      studentManifests.set(studentId, combinedManifest);
      studentTeacherResources.set(studentId, combinedTeacherResources);
    });
  });

  const updatedManifests = Array.from(studentManifests.entries()).map(
    ([studentId, manifest]) => ({
      studentId,
      manifest,
      teacherResources: studentTeacherResources.get(studentId) || [],
    })
  );

  return updateStudentManifests(updatedManifests, lesson, dispatch);
};

export const handleBothOverAndUnderbooked = async (
  overbookedStudentIds: string[],
  underbookedStudentIds: string[],
  lesson: LessonDetails,
  dispatch: any,
  skills: any[],
) => {
  const studentManifests = new Map<string, any[]>();
  const studentTeacherResources = new Map<string, string[]>();

  lesson.skills?.forEach((skill) => {
    if (!skill.skillId) return;
    const skillId = skill.skillId;

    const studentsNeedingSkill =
      lesson.students?.filter((student) => {
        const bookingData = calculateStudentSkillBookingData(
          student.id,
          skillId,
          lesson,
          skills,
        );
        return (
          bookingData &&
          ((overbookedStudentIds.includes(student.id) &&
            bookingData.excessTime > 0) ||
            (underbookedStudentIds.includes(student.id) &&
              bookingData.shortfallTime > 0))
        );
      }) || [];

    for (const student of studentsNeedingSkill) {
      const studentAssignment = getStudentAssignmentFromSkillData(
        student.id,
        lesson
      );
      if (!studentAssignment) continue;

      const mostCommonGrade = getMostCommonGrade(lesson.students.map(s => ({ grade: s.grade.toString() })));
      const gradeLevel = mostCommonGrade
        ? [{ grade: mostCommonGrade.toString() }]
        : [];

      const bookingData = calculateStudentSkillBookingData(
        student.id,
        skillId,
        lesson,
        skills,
      );
      if (!bookingData) continue;

      let currentManifest = normalizeManifest(studentAssignment.manifest || []);
      let newTeacherResources = [...studentAssignment.resources || []];
      let hasChanges = false;

      if (
        overbookedStudentIds.includes(student.id) &&
        bookingData.excessTime > 0
      ) {
        const balanceResult = balanceCategoriesForRemovalBySkill(
          currentManifest,
          skillId,
          bookingData.excessTime,
          gradeLevel,
          newTeacherResources
        );

        currentManifest = balanceResult.manifest;
        let totalTimeRemoved = balanceResult.timeRemoved;

        if (totalTimeRemoved < bookingData.excessTime) {
          const remainingToRemove = bookingData.excessTime - totalTimeRemoved;
          const priorityResult = removeByPriorityForSkill(
            currentManifest,
            skillId,
            remainingToRemove,
            gradeLevel,
            student.first_name + " " + student.last_name ||
              `Student ${student.id}`,
            newTeacherResources
          );
          currentManifest = priorityResult.manifest;
        }
        hasChanges = true;
      }

      if (
        underbookedStudentIds.includes(student.id) &&
        bookingData.shortfallTime > 0
      ) {
        const skillResources = getSkillResources({
          skillId: skillId,
          skillResources: lesson.skillResources,
          lessonName: lesson.lessonName || lesson.name,
          curriculumId: lesson.curriculumId,
          lessonSkills: lesson.skills.filter(s =>s.skillType === SkillType.FOCUS).map(s => ({skillId: s.skillId || "", displayName: s.displayName || ""}))
        });

        const availableResources: any[] = [];
        skillResources.forEach((resource) => {
          const storyId =
            resource.storyIds && resource.storyIds.length > 0
              ? resource.storyIds[0]
              : resource.resourceUniqueId;

          const manifestEntries = getManifestByResourceType(resource, skillId);
          const isDuplicate = isResourceDuplicate(
            manifestEntries,
            currentManifest,
            skillId
          );

          if (!isDuplicate && storyId) {
            availableResources.push({
              ...resource,
              targetSkillId: skillId,
            });
          }
        });

        if (availableResources.length > 0) {
          const balanceResult = balanceCategoriesForAdditionBySkill(
            currentManifest,
            availableResources,
            skillId,
            bookingData.shortfallTime,
            gradeLevel,
            newTeacherResources,
            lesson.skills.length === 1
          );

          currentManifest = balanceResult.manifest;
          newTeacherResources = balanceResult.teacherResources;
          let remainingShortfall = balanceResult.remainingShortfall;

          if (remainingShortfall > 0) {
            const priorityResult = addByPriorityForSkill(
              currentManifest,
              availableResources,
              skillId,
              remainingShortfall,
              gradeLevel,
              newTeacherResources,
              lesson.skills.length === 1
            );
            currentManifest = priorityResult.manifest;
            newTeacherResources = priorityResult.teacherResources;
          }
          hasChanges = true;
        }
      }

      if (hasChanges) {
        const existingManifest = studentManifests.get(student.id) || [];
        const existingTeacherResources = studentTeacherResources.get(student.id) || [];

        const filteredExistingManifest = existingManifest.filter(
          (entry) =>
            !entry.skills?.some((skill: any) => skill.skillId === skillId)
        );
        const filteredExistingTeacherResources = existingTeacherResources.filter(
          (resource) => !newTeacherResources.includes(resource)
        );

        const filteredCurrentManifest = currentManifest.filter((item) =>
          item.skills?.some((skill: any) => skill.skillId === skillId)
        );

        const combinedManifest = [
          ...filteredExistingManifest,
          ...filteredCurrentManifest,
        ];
        const combinedTeacherResources = [
          ...filteredExistingTeacherResources,
          ...newTeacherResources,
        ];

        studentManifests.set(student.id, combinedManifest);
        studentTeacherResources.set(student.id, combinedTeacherResources);
      }
    }
  });

  const updatedManifests = Array.from(studentManifests.entries()).map(
    ([studentId, manifest]) => ({
      studentId,
      manifest,
      teacherResources: studentTeacherResources.get(studentId) || [],
    })
  );

  await updateStudentManifests(updatedManifests, lesson, dispatch);

  return {
    overbookedCount: overbookedStudentIds.length,
    underbookedCount: underbookedStudentIds.length,
  };
};

  